/**
 * @file guideSelectors.js
 * @description Pure selection logic for the onboarding feature guide.
 *
 * Kept separate from both the content (guideSteps.js) and the presentation
 * (FeatureGuideDialog.js) so the capability rules are trivially unit-testable
 * and have a single responsibility. No React, no side effects.
 */

import { labelSourceFor } from "../components/leads/labels/labelsModel";

/**
 * Maps a content capability key → a predicate over the user object.
 *
 * Most predicates read exactly one capability flag already surfaced on the
 * user (see backend /users/me). Adding a new gateable capability is a one-line
 * change here; content files reference the key by string.
 *
 * `no_profession` USED TO LIVE HERE and is deliberately gone (PR E): the tour's
 * own "set a profession" step was the third surface asking the same question,
 * and it is the one that could not act on the answer - it had no CTA, because a
 * CTA that early let a user leave the tour after two steps and never see it
 * again. The welcome dialog and the inline empty-state form are now the two
 * asks, and both submit. An unknown key fails closed, so the deleted predicate
 * cannot leak a step even if a content file still referenced it.
 */
const CAPABILITY_PREDICATES = {
  bi: (user) => Boolean(user?.has_active_bi_monitor),
  intervention: (user) => Boolean(user?.intervention_enabled),
  admin: (user) => Boolean(user?.is_admin),
  recommender: (user) => Boolean(user?.is_recommender),
  // follow-up reminders: Professional/Enterprise + any trialing user (not paid Solo)
  followup: (user) => Boolean(user?.follow_up_eligible),
  // additional notification recipients: a paid Professional/Enterprise feature
  // (Solo/Trial get 0 extra recipients, so the card is hidden from them).
  additional_emails: (user) =>
    ["professional", "enterprise"].includes(user?.user_type),
  // business-website builder: Professional/Enterprise + trialing users (backend
  // centralizes the tier logic into the single `website_eligible` flag).
  website: (user) => Boolean(user?.website_eligible),
  // supporter network: every lead-eligible tier (trial 1 / solo 2 / pro 5 /
  // ent 20 contacts). The backend derives the single `supporter_network_eligible`
  // flag from can_use_supporter_network, so no tier literal appears here.
  supporters: (user) => Boolean(user?.supporter_network_eligible),
  // trial LIFECYCLE, not a tier: a trialing user is user_type='solo' with
  // status='trial', so this reads `status` and never a user_type literal
  // (see the entitlements rule in CLAUDE.md). Gates trial-only guidance, which
  // stops being true the moment the trial converts or lapses.
  trial: (user) => user?.status === "trial",
  // lead labels: the dashboard's OWN labels gate (Dashboard.js
  // `labelsAllowedFor`), read through the one predicate that defines it -
  // signed in and not lapsed. "mine" because the tour describes the user's own
  // feed; an admin gets labels there too. Imported, never re-derived.
  labels: (user) => labelSourceFor(user, "mine") === "viewer",
};

/**
 * Does the user satisfy every capability a step requires?
 *
 * Admins are NOT auto-granted feature capabilities — a step that requires
 * 'bi' still needs has_active_bi_monitor. (Admins reach those pages via the
 * admin override in the navbar, but the guide describes a user's own
 * features, so we gate on the real capability flags.) Unknown keys fail
 * closed (the step is hidden) so a typo can never leak a card to everyone.
 *
 * @param {object|null} user - the current user (capability flags included)
 * @param {string[]} [requires] - capability keys; all must hold
 * @returns {boolean}
 */
function userSatisfies(user, requires) {
  if (!requires || requires.length === 0) return true;
  return requires.every((key) => {
    const predicate = CAPABILITY_PREDICATES[key];
    return typeof predicate === "function" ? predicate(user) : false;
  });
}

/**
 * Filter guide steps down to those the given user is allowed to see, preserving
 * source order.
 *
 * @param {Array<object>} steps - the full GUIDE_STEPS list
 * @param {object|null} user - the current user
 * @returns {Array<object>} the visible subset (never null)
 */
export function selectVisibleSteps(steps, user) {
  if (!Array.isArray(steps)) return [];
  return steps.filter((step) => userSatisfies(user, step.requires));
}

/**
 * Decide what to do with the guide on an authenticated load. Pure so the
 * branching is unit-testable in isolation from React/effects.
 *
 * Returns one of:
 *   'open'      — the user hasn't seen this version and has visible steps.
 *   'mark-seen' — hasn't seen it but has NO visible steps; record the version
 *                 anyway so we don't re-evaluate on every load.
 *   'skip'      — still loading, logged out, already seen this version, or
 *                 already auto-opened this session.
 *
 * Ordering rule (2026-08 swap, REVISED by PR E): the tour used to own the
 * first-login slot unconditionally, because it was the only thing that could
 * tell a brand-new user that a profession must be set before anything can be
 * matched. The WELCOME DIALOG now does that, and it can also take the answer -
 * so login 1 belongs to the dialog and the tour moves to login 2.
 *
 * `welcomeOwnsSession` is what enforces it, and it must be TRUE both while the
 * dialog is due AND for the rest of a session in which it has already been
 * shown: a user who closes the dialog has, in that same tick, an optimistically
 * stamped `onboarding_welcome_seen_at`, so "is the dialog due" alone would flip
 * to false and hand the tour the remainder of login 1 - which is exactly the
 * stacked-modal pattern this queue exists to prevent. GuideProvider passes
 * `shouldShowWelcome(user) || welcomeShownThisSession()`.
 *
 * IMPORTANT: it returns 'skip', NOT 'mark-seen'. Marking the version seen here
 * would satisfy the announce dialogs' gate (`>= currentVersion`) and hand login
 * 2 to an announcement instead of to the tour, permanently skipping the tour
 * for every new user. 'skip' leaves the version untouched, so the tour is
 * simply due on the next session - which is the intended queue.
 *
 * The "what's new" dialogs wait until the guide version has been seen (see
 * `shouldShowPushAnnounce`), making those two conditions exact complements:
 * this function only ever acts while the stored version is BELOW current, and
 * the announcements only ever show at or above it, so they can never fire
 * together.
 *
 * @param {object} args
 * @param {boolean} args.loading           - auth still resolving
 * @param {boolean} args.isAuthenticated
 * @param {object|null} args.user
 * @param {number} args.visibleStepsCount  - steps the user can see
 * @param {boolean} args.alreadyAutoOpened - session guard already set
 * @param {number} args.currentVersion     - CURRENT_GUIDE_VERSION
 * @param {boolean} [args.welcomeOwnsSession] - the welcome dialog has this
 *                                          session's modal slot
 * @returns {'open'|'mark-seen'|'skip'}
 */
export function decideAutoOpen({
  loading,
  isAuthenticated,
  user,
  visibleStepsCount,
  alreadyAutoOpened,
  currentVersion,
  welcomeOwnsSession = false,
}) {
  if (loading || !isAuthenticated || !user) return "skip";
  if ((user.onboarding_guide_version || 0) >= currentVersion) return "skip";
  if (alreadyAutoOpened) return "skip";
  if (welcomeOwnsSession) return "skip";
  return visibleStepsCount > 0 ? "open" : "mark-seen";
}

/**
 * Decide whether the push-notifications "what's new" dialog may open on this load.
 *
 * FIRST of the two "what's new" dialogs left in the login queue:
 *   1. the tour               (decideAutoOpen - owns login #1 outright)
 *   2. this announcement
 *   3. the labels announce    (shouldShowLabelsAnnounce - yields to this one)
 * At most ONE announce dialog may appear per login.
 *
 * The website-builder promo and the supporter-network announcement that used
 * to queue AHEAD of this one are gone (removed for every user, 2026-11), and
 * with them the two yields this function honoured. The tour and the /settings
 * and /supporter-network pages remain the places those features are explained.
 *
 * NEVER for a trialing user (`status === "trial"` - a lifecycle STATUS, not a
 * tier, so no user_type literal is involved). The dashboard's first-steps card
 * asks a trialing user for push as one of its steps, and a modal saying the
 * same thing on top of it is the stacked ask this queue exists to prevent. A
 * user who converts reaches this dialog on a later login, exactly like a user
 * who was never a trial.
 *
 * The tour gate is shared with shouldShowLabelsAnnounce: the stored guide
 * version is at or above current (the exact complement of decideAutoOpen's own
 * condition - the stored version, not a login counter, because the auth layer
 * rotates `previous_login_at` mid-session), plus both same-session guards.
 * `guideActed` closes the one same-session hole: decideAutoOpen's invisible
 * 'mark-seen' writes the version optimistically without setting
 * `guideAutoOpened`, so without it this dialog would take the tour's own turn.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.guideAutoOpened - tour auto-opened this session (state)
 * @param {boolean} args.guideActed      - guide acted this session (session key)
 * @param {boolean} args.announceSeen    - this dialog was dismissed (localStorage)
 * @param {number} args.currentVersion   - CURRENT_GUIDE_VERSION
 * @returns {boolean}
 */
export function shouldShowPushAnnounce({
  user,
  guideAutoOpened,
  guideActed,
  announceSeen,
  currentVersion,
}) {
  if (!user) return false;
  // Trial users get push from the first-steps card instead (see above).
  if (user.status === "trial") return false;
  if (announceSeen) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  return true;
}

/**
 * Decide whether the lead-labels "what's new" dialog may open on this load.
 *
 * LAST in the login queue:
 *   1. the tour               (decideAutoOpen - owns login #1 outright)
 *   2. the push announce      (shouldShowPushAnnounce)
 *   3. this announcement
 * At most ONE announce dialog may appear per login.
 *
 * Gates, in order:
 *   * the labels gate itself - the same predicate the dashboard uses
 *     (`labelSourceFor(user, "mine") === "viewer"`: signed in, not lapsed);
 *   * `labelsAvailable` - the dashboard's labels list has LOADED and holds at
 *     least one label. The copy talks about labels that are already there, so a
 *     failed seed or an empty list must not get a dialog about an empty feature;
 *   * EXISTING users only - `created_at` strictly before `launchCutoff`. A
 *     missing or unparseable `created_at` fails closed. Newer accounts learn
 *     labels from the `lead-labels` tour step instead;
 *   * the tour gate, identical to shouldShowPushAnnounce's (stored guide
 *     version at or above current, plus both same-session guards);
 *   * the push yield. Every user yields to the push announce until it has been
 *     seen. A trialing user (whom the push announce skips) therefore also waits
 *     here until the trial converts - deliberate: the queue order stays push
 *     then labels for everyone, and the labels cutoff predates every live trial.
 *
 * IMPORTANT - `pushAnnounceSeen` must be the value observed at the START of the
 * session (LabelsAnnounce snapshots it in sessionStorage), never a live read:
 * the push dialog writes its key when it is DISMISSED, and a live read would
 * open this dialog right behind the one the user just closed.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.labelsAvailable   - labels loaded AND at least one
 * @param {boolean} args.guideAutoOpened   - tour auto-opened this session (state)
 * @param {boolean} args.guideActed        - guide acted this session (session key)
 * @param {boolean} args.announceSeen      - this dialog was dismissed (localStorage)
 * @param {boolean} args.pushAnnounceSeen  - push announce dismissed AT SESSION START
 * @param {number} args.currentVersion     - CURRENT_GUIDE_VERSION
 * @param {number} args.launchCutoff       - epoch ms; accounts created at or
 *                                           after it are "new"
 * @returns {boolean}
 */
export function shouldShowLabelsAnnounce({
  user,
  labelsAvailable,
  guideAutoOpened,
  guideActed,
  announceSeen,
  pushAnnounceSeen,
  currentVersion,
  launchCutoff,
}) {
  if (!user) return false;
  if (labelSourceFor(user, "mine") !== "viewer") return false;
  if (!labelsAvailable) return false;
  if (announceSeen) return false;
  // Existing users only. Fail closed on anything that is not a real instant.
  const createdAt =
    typeof user.created_at === "string" ? Date.parse(user.created_at) : NaN;
  if (!Number.isFinite(createdAt) || !Number.isFinite(launchCutoff)) return false;
  if (createdAt >= launchCutoff) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  // Yield to the push announce while it still has a turn coming.
  if (!pushAnnounceSeen) return false;
  return true;
}

// Exported for unit testing only.
export const __testables = { userSatisfies, CAPABILITY_PREDICATES };
